import { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, notifyError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { fmtMoney } from "../lib/currency";
import {
  DEFAULT_PAYMENT_METHOD,
  PAYMENT_METHODS,
  paymentMethodLabel,
} from "../lib/paymentMethods";
import {
  generateDepositReceipt,
  generateDeliveryReceipt,
  generateQrLabel,
  generateLargeLabel,
  generateLargeQrLabel,
  generateLargeQrLocationLabel,
} from "../lib/pdf";
import {
  ArrowLeft,
  Printer,
  PackageCheck,
  PackagePlus,
  Loader2,
  Wallet,
  CalendarDays,
  Tag,
  Boxes,
  Trash2,
  FileDown,
  MapPin,
  Pencil,
} from "lucide-react";

function fmtTRY(n) {
  return new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 2 }).format(n || 0);
}

function fmtAmt(n, currency) {
  return fmtMoney(n, currency || "TRY");
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
}

export default function StorageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canManage, defaultCurrency, user } = useAuth();
  const [storage, setStorage] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCheckout, setShowCheckout] = useState(false);
  const [checkoutNotes, setCheckoutNotes] = useState("");
  const [showPay, setShowPay] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [payMethod, setPayMethod] = useState(DEFAULT_PAYMENT_METHOD);
  const [showDelete, setShowDelete] = useState(false);
  const [showLabelPicker, setShowLabelPicker] = useState(false);
  // Etiket basıldıktan SONRA depo/raf değiştiyse gösterilecek uyarı.
  // { run: () => Promise } — kullanıcı onaylayınca çıktı yine de alınır.
  const [labelWarning, setLabelWarning] = useState(null);
  const [busy, setBusy] = useState(false);
  // Depo / raf düzenleme — makbuz veya etiket basılmış olsa bile açılabilir;
  // yalnızca bu iki alan güncellenir (PATCH /storages/{id}/location).
  const [showLoc, setShowLoc] = useState(false);
  const [warehouses, setWarehouses] = useState([]);
  const [locForm, setLocForm] = useState({ warehouse_id: "", location: "" });
  const [freeLocs, setFreeLocs] = useState([]);
  const [freeLocsOpen, setFreeLocsOpen] = useState(false);
  const [freeLocsLoading, setFreeLocsLoading] = useState(false);
  const qrRef = useRef(null);

  // QR encodes the public storage detail URL so scanning opens the page
  // directly in a browser (subject to login). In production this resolves to
  // https://www.lastikoteli.esnekyazilim.com.tr/emanetler/{id}.
  const qrValue = `${window.location.origin}/emanetler/${id}`;

  // Ayarlar > "Çıktılarda Görünecek İletişim Bilgileri". Backend boş alanlar
  // için mevcut yönetici/işletme bilgisine düşmüş hâlini gönderir.
  const outputContact = {
    phone: user?.receipt_phone,
    email: user?.receipt_email,
    website: user?.receipt_website,
    address: user?.receipt_address,
  };

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        api.get(`/storages/${id}`),
        // Yalnızca emanet tahsilatları; satış tahsilatları bu ekranda gösterilmez.
        api.get("/payments?source=emanet"),
      ]);
      setStorage(s.data);
      setPayments(p.data.filter((x) => x.storage_id === id));
    } catch (e) {
      notifyError(e);
      navigate("/emanetler");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const openLocDialog = async () => {
    setLocForm({
      warehouse_id: storage?.warehouse_id || "",
      location: storage?.location || "",
    });
    setFreeLocsOpen(false);
    setShowLoc(true);
    if (!warehouses.length) {
      try {
        const { data } = await api.get("/warehouses");
        setWarehouses(data);
      } catch (e) {
        notifyError(e);
      }
    }
  };

  // Emanetler ekranındaki "Boş yer göster" akışıyla AYNI uç kullanılır.
  const showFreeLocations = async () => {
    if (!locForm.warehouse_id) {
      toast.error("Önce depo seçin");
      return;
    }
    if (freeLocsOpen) {
      setFreeLocsOpen(false);
      return;
    }
    setFreeLocsLoading(true);
    try {
      const { data } = await api.get(
        `/warehouses/${locForm.warehouse_id}/free-locations`
      );
      setFreeLocs(data.locations || []);
      setFreeLocsOpen(true);
    } catch (e) {
      notifyError(e);
    } finally {
      setFreeLocsLoading(false);
    }
  };

  const saveLocation = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/storages/${id}/location`, {
        warehouse_id: locForm.warehouse_id || null,
        location: locForm.location.trim() || null,
      });
      // Emanet id'si ve diğer alanlar değişmez; yanıtı doğrudan kullanırız.
      setStorage(data);
      toast.success("Depo / raf güncellendi");
      setShowLoc(false);
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  const checkout = async () => {
    setBusy(true);
    try {
      await api.post(`/storages/${id}/checkout`, { notes: checkoutNotes });
      toast.success("Lastik teslim edildi");
      setShowCheckout(false);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  // Teslim işlemini yapar, ardından aynı müşteri ve araç seçili şekilde yeni
  // emanet kaydı ekranını açar (hızlı emanet / sezon değişimi akışı).
  const checkoutAndRedeposit = async () => {
    setBusy(true);
    try {
      await api.post(`/storages/${id}/checkout`, { notes: checkoutNotes });
      toast.success("Lastik teslim edildi, yeni emanet kaydı açılıyor");
      setShowCheckout(false);
      const cid = storage.customer_id;
      const vid = storage.vehicle_id || "";
      navigate(
        `/emanetler?customer=${encodeURIComponent(cid)}${vid ? `&vehicle=${encodeURIComponent(vid)}` : ""}`
      );
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  const addPayment = async () => {
    setBusy(true);
    try {
      await api.post("/payments", {
        storage_id: id,
        amount: Number(payAmount) || 0,
        method: payMethod,
      });
      toast.success("Ödeme kaydedildi");
      setShowPay(false);
      setPayAmount("");
      setPayMethod(DEFAULT_PAYMENT_METHOD);
      await load();
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/storages/${id}`);
      toast.success("Emanet silindi");
      navigate("/emanetler");
    } catch (err) {
      notifyError(err);
    }
  };

  const downloadDepositPDF = async () => {
    if (!storage) return;
    try {
      await generateDepositReceipt({
        storage,
        tenantName: user?.tenant_name,
        defaultCurrency,
        qrSvgEl: qrRef.current?.querySelector("svg"),
        contact: outputContact,
      });
    } catch (e) {
      toast.error("Makbuz oluşturulamadı");
    }
  };

  const downloadDeliveryPDF = async () => {
    if (!storage) return;
    try {
      await generateDeliveryReceipt({
        storage,
        tenantName: user?.tenant_name,
        defaultCurrency,
        qrSvgEl: qrRef.current?.querySelector("svg"),
        contact: outputContact,
      });
    } catch (e) {
      toast.error("Teslim makbuzu oluşturulamadı");
    }
  };

  /**
   * Etiket üretimini saran ortak akış:
   *   1) Daha önce etiket alınmış VE o günden beri depo/raf değişmişse önce
   *      uyarı gösterilir (kullanıcı onaylayınca devam eder).
   *   2) PDF üretilir.
   *   3) Backend'e "etiket alındı" damgası atılır → bir sonraki değişiklik
   *      yeniden uyarı üretebilsin.
   * Damgalama başarısız olursa çıktı yine de verilir (uyarı mekanizması
   * kritik değil, kullanıcıyı engellemez).
   */
  const labelLocationChanged = () => {
    if (!storage?.last_label_warehouse_id && !storage?.last_label_location) {
      return false; // hiç etiket alınmamış
    }
    const sameWarehouse =
      (storage.last_label_warehouse_id || null) === (storage.warehouse_id || null);
    const sameLocation =
      (storage.last_label_location || "") === (storage.location || "");
    return !(sameWarehouse && sameLocation);
  };

  const runLabel = async (produce) => {
    try {
      await produce();
    } catch (e) {
      toast.error("Etiket oluşturulamadı");
      return;
    }
    setShowLabelPicker(false);
    try {
      const { data } = await api.post(`/storages/${id}/label-printed`);
      setStorage(data);
    } catch {
      /* damga atılamadı — çıktı zaten alındı, akış engellenmez */
    }
  };

  const withLabelWarning = (produce) => {
    if (labelLocationChanged()) {
      setLabelWarning({ run: () => runLabel(produce) });
      return;
    }
    runLabel(produce);
  };

  const downloadQrLabel = () => {
    if (!storage) return;
    withLabelWarning(() =>
      generateQrLabel({
        storage,
        qrSvgEl: qrRef.current?.querySelector("svg"),
        contact: outputContact,
      })
    );
  };

  const downloadLargeLabel = () => {
    if (!storage) return;
    withLabelWarning(() =>
      generateLargeLabel({
        storage,
        tenantName: user?.tenant_name,
        tenantEmail: user?.tenant_email,
        tenantPhone: user?.tenant_phone,
        contact: outputContact,
        defaultCurrency,
      })
    );
  };

  const downloadLargeQrLabel = () => {
    if (!storage) return;
    withLabelWarning(() =>
      generateLargeQrLabel({
        storage,
        tenantName: user?.tenant_name,
        tenantEmail: user?.tenant_email,
        tenantPhone: user?.tenant_phone,
        contact: outputContact,
        qrSvgEl: qrRef.current?.querySelector("svg"),
        defaultCurrency,
      })
    );
  };

  const downloadLargeQrLocationLabel = () => {
    if (!storage) return;
    withLabelWarning(() =>
      generateLargeQrLocationLabel({
        storage,
        tenantName: user?.tenant_name,
        tenantEmail: user?.tenant_email,
        tenantPhone: user?.tenant_phone,
        contact: outputContact,
        qrSvgEl: qrRef.current?.querySelector("svg"),
      })
    );
  };

  if (loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  if (!storage) return null;

  const remaining = Math.max(0, (storage.fee || 0) - (storage.paid_amount || 0));

  const seasonShort = (s) => (s === "yaz" ? "Yaz" : s === "kis" ? "Kış" : "4 Mev");
  const items =
    storage.items && storage.items.length
      ? storage.items
      : [
          {
            brand: storage.brand,
            size: storage.size,
            dot: storage.dot,
            tread_depth_mm: storage.tread_depth_mm,
            quantity: storage.quantity,
            season: storage.season,
          },
        ];
  const multi = items.length > 1;
  const seasonsLabel = [...new Set(items.map((i) => i.season))].map(seasonShort).join(", ");

  return (
    <div className="p-4 sm:p-8 max-w-5xl mx-auto space-y-6" data-testid="storage-detail-page">
      <Button asChild variant="ghost" size="sm" className="rounded-sm -ml-3 no-print" data-testid="back-btn">
        <Link to="/emanetler"><ArrowLeft className="h-4 w-4 mr-1" /> Emanetler</Link>
      </Button>

      <div className="grid lg:grid-cols-[1fr_300px] gap-6">
        <Card className="rounded-sm border-border p-6 space-y-5">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <div className="label-eyebrow text-amber-600">Emanet Detayı</div>
              {storage.status === "depoda" ? (
                <div className="flex flex-col items-start gap-0.5">
                  <Badge className="rounded-sm bg-amber-500 text-white hover:bg-amber-500">Depoda</Badge>
                  {storage.warehouse_name && (
                    <span className="text-[10px] text-muted-foreground font-mono uppercase tracking-wider">
                      {storage.warehouse_name}
                    </span>
                  )}
                </div>
              ) : (
                <Badge variant="secondary" className="rounded-sm">Teslim Edildi</Badge>
              )}
            </div>
            {multi ? (
              <>
                <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
                  {items.length} Çeşit Lastik
                </h1>
                <div className="font-mono text-lg text-muted-foreground mt-1">Toplam {storage.quantity} adet</div>
              </>
            ) : (
              <>
                <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">
                  {storage.brand}
                </h1>
                <div className="font-mono text-lg text-muted-foreground mt-1">{storage.size}</div>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat icon={Boxes} label="Adet" value={storage.quantity} />
            <Stat icon={Tag} label="Sezon" value={multi ? seasonsLabel : seasonShort(storage.season)} />
            <Stat icon={Boxes} label="Depo" value={storage.warehouse_name || "—"} />
            <Stat icon={MapPin} label="Raf" value={storage.location || "—"} />
          </div>

          {/* Depo / raf sonradan değiştirilebilir — makbuz veya etiket
              basılmış olsa bile. Emanet id'si ve geçmiş kayıtlar değişmez. */}
          <div>
            <Button
              variant="outline"
              size="sm"
              className="rounded-sm"
              onClick={openLocDialog}
              data-testid="edit-location-btn"
            >
              <Pencil className="h-3.5 w-3.5 mr-2" /> Depo / Raf Değiştir
            </Button>
          </div>

          {/* Lastik çeşitleri (çoklu kayıt) */}
          {multi && (
            <div className="border border-border rounded-sm overflow-hidden" data-testid="storage-items">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left px-3 py-2 label-eyebrow">Marka</th>
                    <th className="text-left px-3 py-2 label-eyebrow">Ebat</th>
                    <th className="text-left px-3 py-2 label-eyebrow">Adet</th>
                    <th className="text-left px-3 py-2 label-eyebrow">Sezon</th>
                    <th className="text-left px-3 py-2 label-eyebrow">DOT</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {items.map((it, i) => (
                    <tr key={i} data-testid={`storage-item-${i}`}>
                      <td className="px-3 py-2 font-medium">{it.brand || "—"}</td>
                      <td className="px-3 py-2 font-mono">{it.size || "—"}</td>
                      <td className="px-3 py-2 font-mono">{it.quantity}</td>
                      <td className="px-3 py-2">{seasonShort(it.season)}</td>
                      <td className="px-3 py-2 font-mono">{it.dot || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!multi && storage.dot && (
            <div className="border border-border rounded-sm p-3 inline-block">
              <div className="label-eyebrow">DOT</div>
              <div className="font-display font-bold text-lg tracking-tight mt-1">{storage.dot}</div>
            </div>
          )}

          <div className="border-t border-border pt-5 space-y-3 text-sm">
            <Field k="Müşteri" v={<Link className="text-foreground hover:text-amber-600 font-semibold" to={`/musteriler/${storage.customer_id}`}>{storage.customer_name}</Link>} />
            <Field k="Telefon" v={<span className="font-mono">{storage.customer_phone}</span>} />
            {storage.plate && <Field k="Plaka" v={<span className="font-mono font-bold tracking-wider">{storage.plate}</span>} />}
            {!multi && storage.tread_depth_mm != null && <Field k="Diş Derinliği" v={`${storage.tread_depth_mm} mm`} />}
            <Field k="Giriş Tarihi" v={fmtDate(storage.check_in_date)} />
            {storage.check_out_date && <Field k="Çıkış Tarihi" v={fmtDate(storage.check_out_date)} />}
            {storage.expected_pickup_date && (
              <Field
                k="Tahmini Teslim"
                v={<span className="inline-flex items-center gap-1.5"><CalendarDays className="h-3.5 w-3.5" /> {new Date(storage.expected_pickup_date).toLocaleDateString("tr-TR")}</span>}
              />
            )}
            {storage.notes && <Field k="Not" v={storage.notes} />}
          </div>

          {/* Payment */}
          <div className="border-t border-border pt-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="label-eyebrow">Ödeme</div>
                <div className="font-display font-bold text-2xl tracking-tight mt-1">
                  {fmtAmt(storage.paid_amount, defaultCurrency)} <span className="text-muted-foreground text-base font-normal">/ {fmtAmt(storage.fee, defaultCurrency)}</span>
                </div>
                {remaining > 0 && (
                  <div className="text-sm text-amber-600 font-semibold mt-1">
                    Kalan: {fmtAmt(remaining, defaultCurrency)}
                  </div>
                )}
              </div>
              <Button
                onClick={() => { setPayAmount(remaining > 0 ? String(remaining) : ""); setShowPay(true); }}
                variant="outline"
                className="rounded-sm"
                data-testid="add-payment-btn"
              >
                <Wallet className="h-4 w-4 mr-2" /> Ödeme Ekle
              </Button>
            </div>
            {payments.length > 0 && (
              <div className="border-t border-border divide-y divide-border">
                {payments.map((p) => (
                  <div key={p.id} className="flex items-center justify-between py-2 text-sm">
                    <div>
                      <div className="font-mono">{fmtAmt(p.amount, p.currency || defaultCurrency)}</div>
                      {/* Bilinmeyen eski değerler olduğu gibi gösterilir */}
                      <div className="text-xs text-muted-foreground">
                        {paymentMethodLabel(p.method)} • {fmtDate(p.date)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex flex-wrap gap-3 no-print pt-3 border-t border-border">
            {storage.status === "depoda" && (
              <Button
                onClick={() => setShowCheckout(true)}
                className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
                data-testid="checkout-btn"
              >
                <PackageCheck className="h-4 w-4 mr-2" /> Teslim Et
              </Button>
            )}
            <Button onClick={downloadDepositPDF} variant="outline" className="rounded-sm" data-testid="download-deposit-receipt-btn">
              <FileDown className="h-4 w-4 mr-2" /> Emanet Makbuzu
            </Button>
            {storage.status === "teslim_edildi" && (
              <Button onClick={downloadDeliveryPDF} variant="outline" className="rounded-sm border-amber-500/40 text-amber-700 hover:bg-amber-50" data-testid="download-delivery-receipt-btn">
                <FileDown className="h-4 w-4 mr-2" /> Teslim Makbuzu
              </Button>
            )}
            {canManage && (
              <Button
                onClick={() => setShowDelete(true)}
                variant="outline"
                className="rounded-sm text-destructive border-destructive/30 hover:bg-destructive/10 hover:text-destructive ml-auto"
                data-testid="delete-storage-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Sil
              </Button>
            )}
          </div>
        </Card>

        {/* QR */}
        <Card className="rounded-sm border-border p-5 h-fit lg:sticky lg:top-6 space-y-3" data-testid="qr-card">
          <div className="label-eyebrow">QR Etiket</div>
          <div ref={qrRef} className="bg-white p-3 border border-border rounded-sm flex items-center justify-center">
            <QRCodeSVG value={qrValue} size={220} bgColor="#FFFFFF" fgColor="#0F172A" level="M" />
          </div>
          <div className="text-center">
            <div className="font-mono text-xs text-muted-foreground break-all">{storage.id.slice(0, 16)}…</div>
          </div>
          <Button
            onClick={() => setShowLabelPicker(true)}
            variant="outline"
            className="w-full rounded-sm no-print"
            data-testid="print-qr-btn"
          >
            <Printer className="h-4 w-4 mr-2" /> Etiket PDF
          </Button>
        </Card>
      </div>

      {/* Checkout dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Lastiği Teslim Et</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Bu lastik müşteriye teslim ediliyor. Bu işlem sonrası emanet "Teslim Edildi" durumuna geçecektir.
            </p>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Not (opsiyonel)</Label>
              <Textarea value={checkoutNotes} onChange={(e) => setCheckoutNotes(e.target.value)} className="rounded-sm" data-testid="checkout-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={checkoutAndRedeposit}
              disabled={busy}
              variant="outline"
              className="rounded-sm sm:mr-auto border-foreground/30"
              data-testid="checkout-and-redeposit"
              title="Teslim edip, aynı müşteri ve araç seçili şekilde yeni emanet kaydı açar"
            >
              <PackagePlus className="h-4 w-4 mr-2" /> Teslim/Hızlı Emanet
            </Button>
            <Button variant="outline" onClick={() => setShowCheckout(false)} className="rounded-sm">İptal</Button>
            <Button onClick={checkout} disabled={busy} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="confirm-checkout">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Teslim Et"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={showPay} onOpenChange={setShowPay}>
        <DialogContent className="rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Ödeme Ekle</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Tutar (TL) *</Label>
              <Input type="number" inputMode="decimal" value={payAmount} onChange={(e) => setPayAmount(e.target.value)} className="rounded-sm" data-testid="pay-amount" />
            </div>
            {/* Şartname #5: serbest metin KALDIRILDI; sabit listeden seçilir. */}
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Tahsilat Şekli *</Label>
              <Select value={payMethod} onValueChange={setPayMethod}>
                <SelectTrigger className="rounded-sm" data-testid="pay-method">
                  <SelectValue placeholder="Seçin" />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem
                      key={m.value}
                      value={m.value}
                      data-testid={`pay-method-${m.value}`}
                    >
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPay(false)} className="rounded-sm">İptal</Button>
            <Button onClick={addPayment} disabled={busy || !payAmount} className="rounded-sm bg-foreground text-background hover:bg-foreground/90" data-testid="confirm-payment">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Label format picker */}
      <Dialog open={showLabelPicker} onOpenChange={setShowLabelPicker}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Etiket Boyutu Seçin</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              onClick={downloadQrLabel}
              className="text-left border border-border hover:border-foreground rounded-sm p-4 transition-colors"
              data-testid="label-qr-5x8-btn"
            >
              <div className="font-display font-bold text-base tracking-tight">5 × 8 cm — Karekodlu</div>
              <p className="text-xs text-muted-foreground mt-1">
                Termal etiket yazıcı için kompakt etiket. Üstte QR kod, altta plaka / müşteri / lastik bilgisi içerir.
              </p>
            </button>
            <button
              type="button"
              onClick={downloadLargeLabel}
              className="text-left border border-border hover:border-foreground rounded-sm p-4 transition-colors"
              data-testid="label-large-10x8-btn"
            >
              <div className="font-display font-bold text-base tracking-tight">10 × 8 cm — Karekodsuz</div>
              <p className="text-xs text-muted-foreground mt-1">
                Büyük okunaklı etiket. BÜYÜK harflerle isim, telefon, plaka; altta tarih, sezon, marka, adet, ölçü bilgisi. Qr kod içermez.
              </p>
            </button>
            <button
              type="button"
              onClick={downloadLargeQrLabel}
              className="text-left border border-border hover:border-foreground rounded-sm p-4 transition-colors"
              data-testid="label-large-10x10-btn"
            >
              <div className="font-display font-bold text-base tracking-tight">10 × 10 cm — Karekodlu</div>
              <p className="text-xs text-muted-foreground mt-1">
                En kapsamlı etiket: 10 × 8 tasarımına ek olarak karekod, depo/raf
                ve firma iletişim bilgileri yer alır.
              </p>
            </button>
            <button
              type="button"
              onClick={downloadLargeQrLocationLabel}
              className="text-left border border-border hover:border-foreground rounded-sm p-4 transition-colors"
              data-testid="label-large-10x10-location-btn"
            >
              <div className="font-display font-bold text-base tracking-tight">
                10 × 10 cm — Karekodlu Müşteri / Raf
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Sade raf etiketi: firma bilgileri ve karekodun altında yalnızca
                müşteri adı ile raf/konum yer alır. Raf bilgisi sığabilecek en
                büyük puntoyla yazılır. Lastik bilgisi içermez.
              </p>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLabelPicker(false)} className="rounded-sm">Vazgeç</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Etiket basıldıktan sonra depo/raf değişti uyarısı (şartname #7).
          Kullanıcı onaylayınca çıktı yine de alınır — engelleyici değildir. */}
      <AlertDialog
        open={!!labelWarning}
        onOpenChange={(o) => !o && setLabelWarning(null)}
      >
        <AlertDialogContent className="rounded-sm" data-testid="label-location-warning">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">
              Depo / Raf değişti
            </AlertDialogTitle>
            <AlertDialogDescription>
              Daha önce bu etiketi PDF olarak kaydettiniz ve sonra Depo/Raf
              bilgisini değiştirdiniz. Eğer yazıcı çıktısı aldıysanız eski
              etiketleri imha etmeniz ve yeni etiketi kullanmanız gerekir.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={(ev) => {
                ev.preventDefault();
                const run = labelWarning?.run;
                setLabelWarning(null);
                run?.();
              }}
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              data-testid="label-warning-continue"
            >
              Anladım, Çıktıyı Al
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Depo / Raf düzenleme — Emanetler ekranındaki seçim mantığının aynısı */}
      <Dialog open={showLoc} onOpenChange={setShowLoc}>
        <DialogContent className="rounded-sm max-w-md" data-testid="location-dialog">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Depo / Raf Değiştir</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Depo</Label>
              <Select
                value={locForm.warehouse_id || ""}
                onValueChange={(v) => {
                  setLocForm((f) => ({ ...f, warehouse_id: v }));
                  setFreeLocsOpen(false);
                }}
              >
                <SelectTrigger className="rounded-sm" data-testid="loc-warehouse">
                  <SelectValue placeholder="Depo seçin" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}{w.is_default ? " (varsayılan)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="label-eyebrow">Raf / Konum</Label>
              <Input
                value={locForm.location}
                onChange={(e) => setLocForm((f) => ({ ...f, location: e.target.value }))}
                placeholder="A-12"
                className="rounded-sm font-mono"
                data-testid="loc-location"
              />
              <button
                type="button"
                onClick={showFreeLocations}
                disabled={freeLocsLoading}
                className="inline-flex items-center gap-1 text-xs font-semibold text-amber-600 hover:text-amber-700 disabled:opacity-50"
                data-testid="loc-free-btn"
              >
                {freeLocsLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <MapPin className="h-3 w-3" />}
                {freeLocsOpen ? "Gizle" : "Boş yer göster"}
              </button>
              {freeLocsOpen && (
                <div className="mt-1.5 rounded-sm border border-border bg-muted/40 p-2" data-testid="loc-free-list">
                  {freeLocs.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Bu depoda boş (daha önce kullanılmış) yer yok.</p>
                  ) : (
                    <div className="flex flex-wrap gap-1.5">
                      {freeLocs.map((loc) => (
                        <button
                          key={loc}
                          type="button"
                          onClick={() => {
                            setLocForm((f) => ({ ...f, location: loc }));
                            setFreeLocsOpen(false);
                          }}
                          className="rounded-sm border border-border bg-card px-2 py-1 text-xs font-mono hover:border-amber-500 hover:text-amber-700"
                          data-testid={`loc-free-${loc}`}
                        >
                          {loc}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">
              Yalnızca depo ve raf bilgisi güncellenir. Emanet numarası, lastik
              ve müşteri bilgileri ile geçmiş kayıtlar değişmez.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoc(false)} className="rounded-sm">
              İptal
            </Button>
            <Button
              onClick={saveLocation}
              disabled={busy}
              className="rounded-sm bg-foreground text-background hover:bg-foreground/90"
              data-testid="loc-save"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Kaydet"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent className="rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display tracking-tight">Emanet silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu işlem geri alınamaz. Tüm hareketler ve ödemeler de silinecektir.
              {" "}Ayrıca bu kayıt silindiğinde; makbuz ve etiket çıktısı aldıysanız sorun
              yaşamamak için çıktıları da imha ediniz!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="border border-border p-3 rounded-sm">
      <div className="flex items-center gap-1.5">
        <Icon className="h-3 w-3 text-muted-foreground" />
        <div className="label-eyebrow">{label}</div>
      </div>
      <div className="font-display font-bold text-xl mt-1 tracking-tight">{value}</div>
    </div>
  );
}

function Field({ k, v }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/60 pb-2">
      <div className="label-eyebrow">{k}</div>
      <div className="text-right">{v}</div>
    </div>
  );
}
